import * as Yup from "yup";
import { addYears, format } from "date-fns";
import qs from "qs";
import { InvoiceFilterModel } from "./InvoiceFilterModel";
import PageRequest from "../PageRequest";
import PageResponse from "../PageResponse";
import EntityId from "../EntityId";
import httpClient from "../config/HttpClient";
import { InvoiceEmailModel } from "./InvoiceEmailModel";

export const INVOICE_URL = "/invoices";

export interface InvoiceCreateData {
  clientId: string;
  date: string;
  price: number | null;
  periodFrom: string;
  periodTo: string;
  jobTitle: string;
  discount: number | null;
  product: string;
  productPeriod: string;
}

export interface InvoiceData extends InvoiceCreateData {
  invoiceNo: string;
  addressee?: string;
  companyTitle?: string;
  companyCode?: string;
  companyVatCode?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  email?: string;
  lastSentDate?: string;
  pvm?: number | null;
  author?: string;
  isLast?: boolean;
}

export const getDefaultInvoiceCreateData = (clientId: string = ""): InvoiceCreateData => {
  const now = new Date();
  const oneYearLater = addYears(now, 1);
  const dateStr = format(now, "yyyy-MM-dd");
  const endDateStr = format(oneYearLater, "yyyy-MM-dd");

  return {
    clientId,
    date: dateStr,
    price: null,
    periodFrom: dateStr,
    periodTo: endDateStr,
    jobTitle: "",
    discount: null,
    product: "Leidinio „Teisės aktų žinios“ prenumerata",
    productPeriod: `${dateStr} - ${endDateStr}`,
  };
};

export const invoiceCreateSchema = Yup.object().shape({
  clientId: Yup.string().required("Vartotojas yra privalomas"),
  date: Yup.date().required("Data yra privaloma"),
  periodFrom: Yup.date().required("Prenumeratos pradžios data yra privaloma"),
  periodTo: Yup.date().required("Prenumeratos pabaigos data yra privaloma"),
  price: Yup.number()
    .typeError("Kaina turi būti validus skaičius")
    .required("Kaina yra privaloma"),
  discount: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? null : value
    )
    .nullable()
    .test(
      "valid-discount",
      "Nuolaida negali būti didesnė už kainą",
      function (value) {
        return !value || this.parent.price > value;
      }
    ),
});

// Class for backward compatibility with existing code
export class InvoiceCreateModel implements InvoiceCreateData {
  clientId!: string;
  date!: string;
  price: number | null = null;
  periodFrom!: string;
  periodTo!: string;
  jobTitle!: string;
  discount: number | null = null;
  product!: string;
  productPeriod!: string;

  constructor(data: Partial<InvoiceCreateData> = {}) {
    const defaults = getDefaultInvoiceCreateData(data.clientId);
    Object.assign(this, { ...defaults, ...data });
  }

  static defaultValue(clientId: string): InvoiceCreateData {
    return createDefaultInvoiceData(clientId);
  }

  static withDates(clientId: string, periodFrom: string, periodTo: string): InvoiceCreateData {
    return createInvoiceWithDates(clientId, periodFrom, periodTo);
  }

  static createSchema = invoiceCreateSchema;

  static async pdfPreview(data: InvoiceCreateData): Promise<string> {
    const response = await httpClient.post(
      `${INVOICE_URL}/preview-pdf`,
      data,
      { responseType: "blob" }
    );
    return URL.createObjectURL(response.data);
  }
}

export const createDefaultInvoiceData = (clientId: string): InvoiceData => ({
  ...getDefaultInvoiceCreateData(clientId),
  invoiceNo: "",
  isLast: false,
});

export const createInvoiceWithDates = (
  clientId: string,
  periodFrom: string,
  periodTo: string
): InvoiceData => ({
  ...createDefaultInvoiceData(clientId),
  periodFrom,
  periodTo,
  productPeriod: `${periodFrom} - ${periodTo}`
});

export const invoiceSchema = Yup.object().shape({
  ...invoiceCreateSchema.fields,
  companyTitle: Yup.string().required("Įmonės pavadinimas yra privalomas"),
  companyCode: Yup.string().required("Įmonės kodas yra privalomas"),
  companyVatCode: Yup.string().required("Įmonės PVM kodas yra privalomas"),
  companyAddress: Yup.string().required("Įmonės adresas yra privalomas"),
  companyPhone: Yup.string().required("Telefono numeris yra privalomas"),
  companyEmail: Yup.string().required("El. pašto adresas yra privalomas"),
});

export class InvoiceModel {
  private static serializeParams(params: any) {
    return qs.stringify(params, {
      serializeDate: (date: Date) => format(date, "yyyy-MM-dd"),
    });
  }

  static async findOne(id: string): Promise<InvoiceData> {
    const response = await httpClient.get<InvoiceData>(`${INVOICE_URL}/${id}`);
    return response.data;
  }

  static getPdfUrl(id: string): string {
    return `/api${INVOICE_URL}/${id}/pdf`;
  }

  static async getInvoicePdfPreview(model: InvoiceData): Promise<string> {
    const response = await httpClient.post(
      `${INVOICE_URL}/${model.invoiceNo}/preview-pdf`,
      model,
      { responseType: "blob" }
    );
    return URL.createObjectURL(response.data);
  }

  static async getPdf(id: string): Promise<string> {
    const response = await httpClient.get(`${INVOICE_URL}/${id}/pdf`, {
      responseType: "blob",
    });
    return URL.createObjectURL(response.data);
  }

  static async create(model: InvoiceCreateData): Promise<EntityId> {
    const response = await httpClient.post<EntityId>(INVOICE_URL, model);
    return response.data;
  }

  static async update(model: InvoiceData): Promise<EntityId> {
    const response = await httpClient.put<EntityId>(
      `${INVOICE_URL}/${model.invoiceNo}`,
      model
    );
    return response.data;
  }

  static async delete(id: string): Promise<void> {
    await httpClient.delete(`${INVOICE_URL}/${id}`);
  }

  static async listAll(
    page: PageRequest,
    filter?: InvoiceFilterModel
  ): Promise<PageResponse<InvoiceData>> {
    const response = await httpClient.get<PageResponse<InvoiceData>>(INVOICE_URL, {
      params: { ...filter, ...page },
      paramsSerializer: this.serializeParams,
    });
    return response.data;
  }

  static async listClient(
    page: PageRequest,
    clientId: string
  ): Promise<PageResponse<InvoiceData>> {
    const response = await httpClient.get<PageResponse<InvoiceData>>(
      `${INVOICE_URL}/clients/${clientId}`,
      { params: page }
    );
    return response.data;
  }

  static async send(
    content: InvoiceEmailModel,
    invoiceNo: string
  ): Promise<EntityId> {
    const response = await httpClient.post(
      `${INVOICE_URL}/${invoiceNo}/send`,
      content
    );
    return response.data.content;
  }
}

export default InvoiceModel;
